/**
 * DashboardKpiStrip — 대시보드 맨 위 «오늘 한눈에» (2026-10-14 워크스페이스 디자인)
 *
 * 레퍼런스 «READY TO SUBMIT 86.8%» 판의 구성을 그대로 옮긴다:
 *   왼쪽  — 오늘 할 일 처리율(큰 숫자) + 오늘 처리한 건수
 *   오른쪽 — 숫자 다섯 개 + 바코드(한 줄 = 할 일 k건: 처리 완료 검정 · 남은 일 호박 · 마감 지남·임박 빨강)
 *
 * ⛔ 숫자 색은 바코드 색과 같은 뜻일 때만 칠한다(남은 일=호박, 마감=빨강). 승인 대기·알림은 바코드에 없으므로 검정.
 * ⛔ 큰 숫자를 늘 붉게 두지 않는다 — 마감 지남이 0 이면 빨강은 화면에서 사라진다.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Button } from '../../../components/ui/shadcn/button';
import { WsBarcode, wsBarcodeMarks, wsTone, type WsTone } from '../../../components/ws';
import type { ActionQueuePayload } from '../../../services/actionQueue';

interface Props {
  queue: ActionQueuePayload | null;
  pendingApprovals: number;
  unreadCount: number;
}

const BARCODE_CAPACITY = 150;

interface Stat {
  key: string;
  label: string;
  value: number;
  tone?: WsTone;
  onClick?: () => void;
  hint?: string;
}

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

const StatCell: React.FC<{ s: Stat }> = ({ s }) => {
  const body = (
    <>
      <span className="ws-eyebrow" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {s.label}
        {s.onClick && <ArrowUpRight size={12} aria-hidden style={{ color: 'var(--ws-ink-4)' }} />}
      </span>
      <span
        className={`ws-num ${s.tone ? wsTone(s.tone) : ''}`}
        style={{
          display: 'block', marginTop: 8, fontSize: 32, lineHeight: 1, fontWeight: 450, letterSpacing: '-0.02em',
          color: s.tone ? 'var(--tone-ink)' : 'var(--ws-ink)',
        }}
      >
        {s.value.toLocaleString()}
      </span>
    </>
  );
  return s.onClick ? (
    <Button
      type="button"
      variant="ghost"
      data-testid={`kpi-small-${s.key}`}
      onClick={s.onClick}
      title={s.hint}
      className="ws-btn ws-btn--ghost"
      style={{ display: 'block', height: 'auto', padding: '4px 8px', margin: '-4px -8px', textAlign: 'left', fontWeight: 400 }}
    >
      {body}
    </Button>
  ) : (
    <div data-testid={`kpi-small-${s.key}`}>{body}</div>
  );
};

export const DashboardKpiStrip: React.FC<Props> = ({ queue, pendingApprovals, unreadCount }) => {
  const navigate = useNavigate();
  const remaining = queue?.total ?? 0;
  const done = queue?.done_today ?? 0;
  const sla = queue?.groups.find((g) => g.key === 'sla')?.count ?? 0;
  const customer = queue?.groups.find((g) => g.key === 'customer_request')?.count ?? 0;
  const all = done + remaining;
  const rate = all > 0 ? (done / all) * 100 : null;
  const urgent = sla > 0;

  const segments = [
    { tone: 'ink' as WsTone, count: done },
    { tone: 'amber' as WsTone, count: Math.max(0, remaining - sla) },
    { tone: 'red' as WsTone, count: sla },
  ];
  const { perMark } = wsBarcodeMarks(segments, BARCODE_CAPACITY);

  const stats: Stat[] = [
    { key: 'all', label: '오늘 들어온 일', value: all },
    { key: 'today', label: '남은 일', value: Math.max(0, remaining - sla), tone: remaining - sla > 0 ? 'amber' : undefined, onClick: () => scrollToId('today-action-queue'), hint: '할 일 목록으로' },
    { key: 'sla', label: '마감 지남·임박', value: sla, tone: urgent ? 'red' : undefined, onClick: () => scrollToId('aq-group-sla'), hint: '해피콜 D+3 · 클레임 D+7 · 설치 지연 · 내 할 일' },
    { key: 'approvals', label: '승인 대기', value: pendingApprovals, onClick: () => navigate('/sales'), hint: '판매 화면으로' },
    { key: 'unread', label: '안읽은 알림', value: unreadCount, onClick: () => navigate('/notifications'), hint: '알림함으로' },
  ];

  return (
    <div className="ws-bezel" data-testid="dashboard-kpi-strip" data-mode={urgent ? 'urgent' : 'calm'}>
      <div className="ws-card dash-hero">
        {/* 왼쪽 — 처리율 */}
        <div className="dash-hero-left" data-testid="kpi-hero">
          <span className="ws-eyebrow">오늘 할 일 처리율</span>
          <div style={{ display: 'flex', alignItems: 'baseline', marginTop: 14 }}>
            <span className="ws-num" style={{ fontSize: 76, lineHeight: 0.95, fontWeight: 450, letterSpacing: '-0.045em' }}>
              {rate == null ? '—' : rate.toFixed(1)}
            </span>
            {rate != null && <span style={{ fontSize: 40, fontWeight: 400, color: 'var(--ws-ink-4)', marginLeft: 2, letterSpacing: '-0.02em' }}>%</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16 }}>
            <span className={`ws-delta ${done > 0 ? '' : wsTone('gray')}`}>+{done.toLocaleString()}건</span>
            <span className="ws-sub">{rate == null ? '오늘 처리할 일이 없습니다' : customer > 0 ? `오늘 처리 · 고객 요청 ${customer}건 남음` : '오늘 처리'}</span>
          </div>
        </div>

        {/* 오른쪽 — 숫자 + 바코드 */}
        <div className="dash-hero-right">
          <div className="dash-hero-stats">
            {stats.map((s) => <StatCell key={s.key} s={s} />)}
          </div>
          <WsBarcode
            segments={segments}
            capacity={BARCODE_CAPACITY}
            label={`처리 완료 ${done}건, 남은 일 ${Math.max(0, remaining - sla)}건, 마감 지남·임박 ${sla}건`}
          />
          <div className="dash-hero-legend">
            <span style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
              <span className="ws-legend ws-tone-ink">처리 완료</span>
              <span className="ws-legend ws-tone-amber">남은 일</span>
              <span className="ws-legend ws-tone-red">마감 지남·임박</span>
            </span>
            <span className="ws-sub">한 줄이 할 일 {perMark}건 · 고객 요청·마감·배정·정기 합산</span>
          </div>
        </div>
      </div>

    </div>
  );
};

export default DashboardKpiStrip;
